import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles, MessageCircleQuestion, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { GoogleGenAI } from '@google/genai';
import { useTheme } from '../context/ThemeContext';
import { useWidget } from '../context/WidgetContext';
import { FAQ } from '../constants/faq';
import ReactMarkdown from 'react-markdown';
import { AI_SYSTEM_PROMPT, FALLBACK_REPLIES, FALLBACK_DEFAULT, COMPANY } from '../constants/knowledge';
import ReactGA from 'react-ga4';

export function ChatWidget() {
  const { activeWidget, toggle, close } = useWidget();
  const isChat = activeWidget === 'chat';
  const isWhatsApp = activeWidget === 'whatsapp';
  const isFaq = activeWidget === 'faq';

  const [messages, setMessages] = useState<{ role: 'user' | 'model'; content: string }[]>([
    { role: 'model', content: `Hi there! 👋 I am your ${COMPANY.name} AI assistant. How can I help you regarding your interior design needs today?` },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [faqActive, setFaqActive] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const confirmWhatsApp = () => { ReactGA.event({ category: 'Engagement', action: 'WhatsApp Chat Opened' }); close(); window.open('https://wa.me/9989697942', '_blank'); };

  // Parse API keys from comma-separated string
  const apiKeys = (import.meta.env.VITE_GEMINI_API_KEY || '').split(',').map(k => k.trim()).filter(Boolean);
  const currentKeyIndexRef = useRef(0);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    const trySendMessage = async (attemptCount: number): Promise<boolean> => {
      if (attemptCount >= apiKeys.length) return false;

      try {
        const apiKey = apiKeys[currentKeyIndexRef.current];
        if (!apiKey) return false;

        const genAI = new GoogleGenAI({ apiKey });
        const chat = genAI.chats.create({
          model: 'gemini-2.5-flash',
          config: { systemInstruction: AI_SYSTEM_PROMPT },
        });

        // Add history for context
        for (const m of messages) {
          if (m.role === 'user') await chat.sendMessage({ message: m.content });
        }

        const response = await chat.sendMessage({ message: userMessage });
        setMessages(prev => [...prev, { role: 'model', content: response.text || "I'm sorry, I couldn't process that." }]);
        return true;
      } catch (error: any) {
        console.error(`Error with API key index ${currentKeyIndexRef.current}:`, error);
        const isRateLimit = error?.message?.includes('429') || error?.status === 429;
        
        if (isRateLimit && apiKeys.length > 1 && attemptCount < apiKeys.length - 1) {
          // Rotate to next key and retry
          currentKeyIndexRef.current = (currentKeyIndexRef.current + 1) % apiKeys.length;
          console.log(`Rate limit hit. Rotating to key index ${currentKeyIndexRef.current}`);
          return await trySendMessage(attemptCount + 1);
        }
        
        // Final failure after all relevant retries
        const errorMessage = isRateLimit 
          ? `The AI is currently at capacity or experiencing high traffic. 😅 Please try again in a moment, or call us at ${COMPANY.phone} for immediate assistance!`
          : `I'm having trouble connecting right now. Please try again or reach us at ${COMPANY.phone}.`;
        setMessages(prev => [...prev, { role: 'model', content: errorMessage }]);
        return false;
      }
    };

    try {
      if (apiKeys.length > 0) {
        await trySendMessage(0);
      } else {
        // Fallback logic when no API keys are provided
        const msg = userMessage.toLowerCase();
        const match = FALLBACK_REPLIES.find(f => f.pattern.test(msg));
        const reply = match ? match.reply : FALLBACK_DEFAULT;
        setTimeout(() => { setMessages(prev => [...prev, { role: 'model', content: reply }]); setIsLoading(false); }, 500);
        return; // Return early to prevent setIsLoading(false) in finally because of the timeout
      }
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Shared card classes ── */
  const cardBg = isDark ? 'bg-[#1E293B] border-gray-700' : 'bg-white border-gray-100';

  return (
    <>
      {/* ── Popups (only one visible at a time) ── */}
      <AnimatePresence>
        {/* Backdrop for outside click */}
        {activeWidget && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40"
            onClick={close}
          />
        )}

        {/* WhatsApp Confirmation */}
        {isWhatsApp && (
          <motion.div key="wa" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.2 }}
            className={`fixed bottom-[232px] right-6 z-60 w-[300px] rounded-2xl shadow-2xl border p-6 ${cardBg}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 bg-green-500 rounded-full flex items-center justify-center text-white shrink-0">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </div>
              <div>
                <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-primary'}`}>Chat on WhatsApp</p>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>You'll be redirected to WhatsApp</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={close} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-white/5' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>Cancel</button>
              <button onClick={confirmWhatsApp} className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-green-500 text-white hover:bg-green-600 transition-colors">Continue</button>
            </div>
          </motion.div>
        )}

        {/* FAQ Panel */}
        {isFaq && (
          <motion.div key="faq" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.2 }}
            className={`fixed bottom-[232px] right-6 z-60 w-[360px] max-h-[480px] rounded-2xl shadow-2xl flex flex-col overflow-hidden border ${cardBg}`}>
            <div className="bg-primary px-5 py-4">
              <strong className="text-white text-base font-poppins block">Have Questions?</strong>
              <span className="text-white/50 text-xs">Quick answers to common queries</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {FAQ.map((faq, i) => (
                <div key={i} className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-50'}`}>
                  <button onClick={() => setFaqActive(faqActive === i ? null : i)}
                    className={`w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium transition-colors ${faqActive === i ? 'text-accent' : isDark ? 'text-gray-200 hover:text-accent' : 'text-primary hover:text-accent'}`}>
                    <span className="pr-3 leading-snug">{faq.q}</span>
                    <ChevronDown className={`w-4 h-4 shrink-0 text-accent transition-transform duration-300 ${faqActive === i ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {faqActive === i && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                        <p className={`px-5 pb-4 text-[13px] leading-relaxed whitespace-pre-line ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
            <div className={`px-5 py-3 border-t text-center ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <a href="https://wa.me/919989697942" target="_blank" rel="noopener noreferrer" className="text-accent text-[13px] font-semibold hover:underline">
                Still have questions? Chat on WhatsApp →
              </a>
            </div>
          </motion.div>
        )}

        {/* AI Chat Window */}
        {isChat && (
          <motion.div key="chat" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.2 }}
            className={`fixed bottom-[232px] right-6 z-60 w-[350px] h-[500px] rounded-2xl shadow-2xl flex flex-col overflow-hidden border ${cardBg}`}>
            <div className="bg-primary p-4 flex justify-between items-center text-white">
              <div className="flex items-center gap-2">
                <Sparkles size={20} className="text-accent" />
                <span className="font-poppins font-semibold">Aura AI Assistant</span>
              </div>
              <button onClick={close} className="hover:text-accent transition-colors"><X size={20} /></button>
            </div>
            <div className={`flex-1 p-4 overflow-y-auto flex flex-col gap-3 ${isDark ? 'bg-[#111827]' : 'bg-gray-50'}`}>
              {messages.map((msg, idx) => (
                <div key={idx} className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? (isDark ? 'bg-accent/15 border border-accent/30 text-white self-end rounded-tr-sm shadow-sm' : 'bg-accent text-white self-end rounded-tr-sm shadow-sm') : isDark ? 'bg-[#0B0F1A] border border-gray-700 text-gray-300 self-start rounded-tl-sm' : 'bg-white border border-gray-200 text-gray-700 self-start rounded-tl-sm'}`}>
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
                      li: ({ children }) => <li className="mb-1">{children}</li>,
                      strong: ({ children }) => <strong className="font-bold text-accent">{children}</strong>,
                    }}
                  >
                    {msg.content}
                  </ReactMarkdown>
                </div>
              ))}
              {isLoading && (
                <div className={`self-start p-3 rounded-2xl rounded-tl-sm text-sm flex gap-1 ${isDark ? 'bg-[#0B0F1A] border border-gray-700 text-gray-400' : 'bg-white border border-gray-200 text-gray-700'}`}>
                  <span className="animate-bounce">.</span><span className="animate-bounce" style={{ animationDelay: '0.2s' }}>.</span><span className="animate-bounce" style={{ animationDelay: '0.4s' }}>.</span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <form onSubmit={handleSend} className={`p-3 border-t flex gap-2 ${isDark ? 'bg-[#1E293B] border-gray-700' : 'bg-white border-gray-100'}`}>
              <input type="text" value={input} onChange={e => setInput(e.target.value)} placeholder="Ask about design or cost..."
                className={`flex-1 px-3 py-2 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-accent ${isDark ? 'bg-[#111827] text-white placeholder-gray-500' : 'bg-gray-100 text-primary'}`} />
              <button type="submit" disabled={isLoading || !input.trim()} className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white disabled:opacity-50">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Floating Buttons — always visible, single stack ── */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-3">
        {/* FAQ - Commented out as FAQ is now directly embedded on the homepage
        <button onClick={() => toggle('faq')} aria-label="FAQ"
          className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 ${isFaq ? 'bg-accent text-white' : isDark ? 'bg-[#1E293B] text-accent shadow-black/40' : 'bg-primary text-white'}`}>
          {isFaq ? <X className="w-5 h-5" /> : <MessageCircleQuestion className="w-5 h-5" />}
        </button>
        */}

        {/* WhatsApp */}
        <button onClick={() => toggle('whatsapp')} aria-label="WhatsApp"
          className="w-12 h-12 bg-green-500 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-green-600 hover:scale-110 transition-all duration-300">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        </button>

        {/* AI Chat */}
        <button onClick={() => toggle('chat')} aria-label="AI Chat"
          className="w-14 h-14 bg-primary rounded-full shadow-lg flex items-center justify-center text-white hover:bg-black hover:scale-110 transition-all duration-300">
          {isChat ? <X size={24} /> : <Sparkles size={24} className="text-accent" />}
        </button>
      </div>
    </>
  );
}
